import React, { useState, useEffect } from "react";
import {
  X, Tag, Plus, Minus, ShieldCheck, ShoppingCart, ChevronDown, ChevronUp,
  Package, Layers, PlusCircle
} from "lucide-react";
import { formatCurrency } from "../../utils/salesUtils";
import { productService } from "../../api/masterService";
import { conversionService } from "../../api/conversionService";
import { getStocks } from "../../api/inventoryService";

/**
 * Unified product selection modal — GoFood/ShopeeFood style.
 *
 * Sections shown conditionally (skip if empty):
 *   1. Varian Merek   — variants where brand_id != null  (retail)
 *   2. Varian Produk  — variants where brand_id == null  (F&B: Pedas, Manis …)
 *   3. Satuan Jual    — unit conversion options
 *   4. Pilihan Extra  — add-ons / options  (each with its own qty control)
 *
 * Props:
 *   isOpen       {boolean}
 *   onClose      {() => void}
 *   product      {object}  — stock row from catalog (has product_id, name, unit, options…)
 *   onConfirm    {(cartItem) => void}
 *   showStock    {boolean}
 */
export default function OptionSelectionModal({
  isOpen,
  onClose,
  product,
  onConfirm,
  showStock: propShowStock,
}) {
  const showStock =
    propShowStock !== undefined
      ? propShowStock
      : (() => {
          const saved = localStorage.getItem("catalog_show_product_stock");
          return saved !== null ? JSON.parse(saved) : true;
        })();

  // ─── State ────────────────────────────────────────────────────────────────
  const [fullProduct, setFullProduct]       = useState(null);
  const [loadingFull, setLoadingFull]       = useState(false);

  // Variant selections
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Unit selection
  const [units, setUnits]               = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [conversionRules, setConversionRules] = useState([]);

  // Extras — map of option.id -> qty (0 = not selected)
  const [extraQty, setExtraQty]       = useState({});
  const [extraExpanded, setExtraExpanded] = useState(false);

  // Variant stock map: variant.id -> current_stock
  const [variantStocks, setVariantStocks] = useState({});

  // Main product qty
  const [quantity, setQuantity] = useState(1);

  // ─── Derived ──────────────────────────────────────────────────────────────
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
  // Varian dari productService.get memakai field id/name, sedangkan data dari
  // endpoint stok memakai variant_id/variant_name — filter harus mendukung
  // dua bentuk agar varian asli tidak ikut terbuang.
  const isRealVariantRow = (v) => {
    const vid = "variant_id" in v ? v.variant_id : v.id;
    const vname = "variant_name" in v ? v.variant_name : v.name;
    return vid != null || (vname && vname !== "DEFAULT");
  };
  const allVariants = (fullProduct?.variants || [])
    .filter(v => !v.unit_id || v.unit_id === fullProduct?.unit_id)
    .filter(v => !isApps || !v.brand_id)
    .filter(isRealVariantRow);
  const addonGroups = fullProduct?.addon_groups || [];

  const hasVariants = allVariants.length > 0;
  const hasExtras   = addonGroups.length > 0;
  const hasUnits    = units.length > 1;

  // ─── Computed price & stock ───────────────────────────────────────────────
  const activeVariant = selectedVariant;

  const baseUnitPrice = (() => {
    if (activeVariant) return activeVariant.sell_price || 0;
    return product?.price || product?.unit_price || fullProduct?.sell_price || 0;
  })();

  // addonSelections: map of addonId -> qty (0 = not selected)
  // For radio groups (max_selection===1): selecting one clears others in the group
  const [addonSelections, setAddonSelections] = useState({});

  const unitFactor = selectedUnit?.factor || 1;

  // Build flat list of all selected addons across groups
  const selectedExtras = Object.entries(addonSelections)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      for (const g of addonGroups) {
        const addon = (g.addons || []).find(a => a.id === id);
        if (addon) return { ...addon, selected_qty: qty, group_id: g.id };
      }
      return null;
    })
    .filter(Boolean);

  const extrasTotal = selectedExtras.reduce(
    (sum, opt) => sum + (opt.sell_price || 0) * opt.selected_qty,
    0
  );

  const matchingRule = selectedUnit && selectedUnit.factor > 1 ? (
    conversionRules.find(r => 
      r.from_unit_id === selectedUnit.id && 
      (r.variant_id === activeVariant?.id || (!r.variant_id && !activeVariant))
    ) || conversionRules.find(r => 
      r.from_unit_id === selectedUnit.id && !r.variant_id
    )
  ) : null;

  const hasCustomPrice = matchingRule && matchingRule.sell_price > 0;
  const finalBasePrice = hasCustomPrice ? matchingRule.sell_price : (baseUnitPrice * unitFactor);
  const finalUnitPrice = finalBasePrice + (extrasTotal * unitFactor);
  const finalTotal     = finalUnitPrice * quantity;

  const baseStock = (() => {
    if (activeVariant) return variantStocks[activeVariant.id] ?? activeVariant.stok ?? 0;
    return product?.stock ?? product?.current_stock ?? 0;
  })();

  // Stok selalu tersimpan dalam satuan dasar (Pcs). Saat satuan jual hasil
  // konversi dipilih (factor > 1), stok yang ditampilkan ikut dikonversi:
  // mis. stok 50 Pcs = 4 Lusin (50 / 12).
  const activeStock = Math.floor(Number(baseStock) / unitFactor);

  const unitLabel = selectedUnit?.name || product?.unit || "Pcs";

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !product) return;

    // Reset all state
    setSelectedVariant(null);
    setAddonSelections({});
    setExtraExpanded(false);
    setQuantity(1);
    setUnits([]);
    setSelectedUnit(null);
    setVariantStocks({});
    setFullProduct(null);

    const productId = product.product_id || product.id;
    if (!productId) return;

    setLoadingFull(true);

    // 1. Fetch full product (variants + options)
    productService.get(productId)
      .then(res => {
        const p = res?.data || res;
        setFullProduct(p);

        const vars = (p.variants || [])
          .filter(v => !v.unit_id || v.unit_id === p.unit_id)
          .filter(v => !isApps || !v.brand_id)
          .filter((v) => {
            const vid = "variant_id" in v ? v.variant_id : v.id;
            const vname = "variant_name" in v ? v.variant_name : v.name;
            return vid != null || (vname && vname !== "DEFAULT");
          });
        if (vars.length > 0) setSelectedVariant(vars[0]);
      })
      .catch(console.error)
      .finally(() => setLoadingFull(false));

    // 2. Fetch conversion units
    const baseUnit = {
      id: product.unit_id,
      name: typeof product.unit === "string" ? product.unit : (product.unit?.name || "Pcs"),
      factor: 1,
    };
    setUnits([baseUnit]);
    setSelectedUnit(baseUnit);

    conversionService.rules.list({ product_id: productId })
      .then(res => {
        const rules = res?.data?.items || res?.data?.data || res?.data || [];
        setConversionRules(rules);
        
        const uniqueUnitsMap = new Map();
        rules.forEach(r => {
          if (!uniqueUnitsMap.has(r.from_unit_id)) {
            uniqueUnitsMap.set(r.from_unit_id, {
              id: r.from_unit_id,
              name: r.from_unit_name || r.from_unit?.name || "Unknown",
              factor: r.conversion_factor,
              sell_price: r.sell_price,
            });
          }
        });
        
        const extras = Array.from(uniqueUnitsMap.values());
        if (extras.length > 0) {
          setUnits(prev => [...prev, ...extras]);
        }
      })
      .catch(console.error);

    // 3. Fetch stocks for all variants
    getStocks({ product_id: productId, limit: 100 })
      .then(res => {
        const stocks = res?.data || [];
        const map = {};
        stocks.forEach(s => {
          if (s.variant_id) map[s.variant_id] = s.current_stock ?? 0;
        });
        setVariantStocks(map);
      })
      .catch(console.error);

  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  // ─── Handlers ─────────────────────────────────────────────────────────────
  // Toggle addon selection:
  //   - radio group (max_selection===1): selecting one clears all others in group
  //   - checkbox group (max_selection>1): toggle on/off, respecting max distinct items
  const handleAddonToggle = (group, addon) => {
    const maxSel = group.max_selection || 1;
    setAddonSelections(prev => {
      const next = { ...prev };
      const isSelected = (next[addon.id] || 0) > 0;

      if (isSelected) {
        // Toggle off (deselect)
        next[addon.id] = 0;
      } else {
        // Toggle on (select with qty = 1)
        if (maxSel === 1) {
          // Radio — clear entire group first
          (group.addons || []).forEach(a => { next[a.id] = 0; });
          next[addon.id] = 1;
        } else {
          // Checkbox — count distinct selected items in group
          const selectedDistinctInGroup = (group.addons || []).filter(a => (next[a.id] || 0) > 0).length;
          const limit = maxSel > 0 ? maxSel : 99;
          if (selectedDistinctInGroup < limit) {
            next[addon.id] = 1;
          }
        }
      }
      return next;
    });
  };

  const handleAddonQtyChange = (group, addon, delta) => {
    setAddonSelections(prev => {
      const next = { ...prev };
      const currentQty = next[addon.id] || 0;
      const nextQty = currentQty + delta;

      if (nextQty <= 0) {
        next[addon.id] = 0;
      } else {
        // Quantity of selected item is not constrained by group max_selection.
        // Only constrained by item's stock or default high limit (e.g. 99).
        const limit = typeof addon.stok === 'number' ? addon.stok : 99;
        if (nextQty <= limit) {
          next[addon.id] = nextQty;
        }
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const variantId   = activeVariant?.id || null;
    const variantName = activeVariant
      ? (activeVariant.brand_name || activeVariant.name || "")
      : "";
    const purchasePrice = activeVariant?.purchase_price
      || fullProduct?.purchase_price
      || product?.purchase_price
      || 0;

    // Build extras as grouped items with qty
    const selectedOptionsGrouped = selectedExtras.map(opt => ({
      id: opt.id,
      name: opt.name,
      price: opt.sell_price || 0,
      sell_price: opt.sell_price || 0,
      purchase_price: opt.purchase_price || 0,
      adjusted_price: (opt.sell_price || 0) * unitFactor,
      qty: opt.selected_qty
    }));

    const extraNamesArr = selectedExtras.map(opt =>
      opt.selected_qty > 1 ? `${opt.name} x${opt.selected_qty}` : opt.name
    );

    const itemName = product.name + (variantName ? ` - ${variantName}` : "");

    onConfirm({
      ...product,
      qty: quantity,
      price: finalUnitPrice,
      purchase_price: purchasePrice * unitFactor,
      variant_id: variantId,
      variant_name: variantName,
      variant_only_name: variantName,
      option_names: extraNamesArr,
      item_name: itemName,
      product_name: product.name,
      unit_id: selectedUnit?.id || product.unit_id,
      unit_name: selectedUnit?.name || unitLabel,
      unit_factor: unitFactor,
      factor: unitFactor,
      category_id: product.category_id || fullProduct?.category_id || product.category || null,
      category_name: product.category_name || fullProduct?.category_name || null,
      category: product.category || fullProduct?.category || null,
      selected_options: selectedOptionsGrouped,
      base_price: finalBasePrice,
    });

    onClose();
  };

  // Confirm is disabled if required group selections missing (min_selection > 0)
  const requiredGroupsMissing = addonGroups.some(g => {
    if ((g.min_selection || 0) === 0) return false;
    const selectedInGroup = (g.addons || []).filter(a => (addonSelections[a.id] || 0) > 0).length;
    return selectedInGroup < g.min_selection;
  });
  const canConfirm =
    (!hasVariants || selectedVariant !== null) &&
    !requiredGroupsMissing;

  // ─── Render helpers ───────────────────────────────────────────────────────
  const totalSelectedAddons = selectedExtras.length;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}>
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl ring-1 ring-slate-200/70 w-full sm:max-w-xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* ── Hero Header ─────────────────────────────────────────────── */}
        <div className="relative bg-gradient-to-br from-teal-700 via-teal-800 to-emerald-900 text-white px-6 pt-5 pb-4 flex-shrink-0">
          {/* Drag handle (mobile) */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-white/30 sm:hidden" />

          <div className="flex items-start justify-between gap-3 mt-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-100/80">
                  Pilih Varian Produk
                </p>
                {product.code && (
                  <span className="px-1.5 py-0.5 bg-white/20 text-white text-[9px] font-mono rounded">
                    {product.code}
                  </span>
                )}
              </div>
              <h3 className="font-bold text-white text-xl leading-tight truncate uppercase">
                {product.name}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-50 bg-emerald-400/20 px-2 py-0.5 rounded-full ring-1 ring-emerald-300/40 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" /> Aktif
                </span>
                {hasVariants && (
                  <span className="text-[11px] text-teal-100/90">
                    {allVariants.length} varian tersedia
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Content ──────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto bg-slate-50/40">
          <div className="p-4 space-y-4">

            {/* ── Ringkasan Pilihan ─────────────────────────────── */}
            <div className="bg-white ring-1 ring-slate-200/70 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-3.5 h-3.5 text-teal-600" />
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">
                  Ringkasan Pilihan
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
                {hasVariants && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Varian</p>
                    <p className="text-sm font-bold text-slate-900 mt-0.5">
                      {selectedVariant?.brand_name || selectedVariant?.name || "—"}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Satuan Jual</p>
                  <p className="text-sm font-bold text-slate-900 mt-0.5">{unitLabel}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Harga</p>
                  <p className="text-sm font-bold text-teal-700 mt-0.5">{formatCurrency(finalUnitPrice)}</p>
                </div>
                {showStock && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Stok Tersedia</p>
                    <p className={`text-sm font-bold mt-0.5 ${activeStock > 0 ? "text-slate-900" : "text-rose-600"}`}>
                      {activeStock} {unitLabel}
                    </p>
                  </div>
                )}
                {totalSelectedAddons > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Extras</p>
                    <p className="text-sm font-bold text-emerald-700 mt-0.5">
                      +{formatCurrency(extrasTotal)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Loading ──────────────────────────────────────── */}
            {loadingFull && (
              <div className="flex items-center justify-center py-6 text-slate-400 text-sm">
                <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Memuat data produk…
              </div>
            )}

            {/* ── Varian ───────────────────────────────── */}
            {hasVariants && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-3.5 h-3.5 text-slate-500" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                    Pilih Varian
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {allVariants.map(v => {
                    const isSelected = selectedVariant?.id === v.id;
                    const stock = variantStocks[v.id] ?? v.stok ?? 0;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVariant(v)}
                        className={`relative text-center rounded-xl ring-1 px-3 py-3 transition-all ${
                          isSelected
                            ? "ring-teal-500 bg-teal-50 shadow-sm"
                            : "ring-slate-200 bg-white hover:ring-teal-200 hover:bg-teal-50/40"
                        }`}>
                        <p className={`text-sm font-bold ${isSelected ? "text-teal-800" : "text-slate-800"}`}>
                          {v.brand_name || v.name}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {formatCurrency(v.sell_price || 0)}
                        </p>
                        {showStock && (
                          <p className={`text-[10px] mt-0.5 ${stock > 0 ? "text-slate-400" : "text-rose-400 font-semibold"}`}>
                            Stok {stock}
                          </p>
                        )}
                        {isSelected && (
                          <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-teal-600 flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── 3. Satuan Jual ────────────────────────────────── */}
            {hasUnits && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 mb-2">
                  Satuan Jual
                </p>
                <div className="flex flex-wrap gap-2">
                  {units.map(u => {
                    const isSel = selectedUnit?.id === u.id;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setSelectedUnit(u)}
                        className={`flex flex-col items-center px-4 py-2.5 rounded-xl ring-1 transition-all text-center ${
                          isSel
                            ? "ring-teal-500 bg-teal-50 shadow-sm"
                            : "ring-slate-200 bg-white hover:ring-teal-200"
                        }`}>
                        <span className={`text-sm font-bold ${isSel ? "text-teal-800" : "text-slate-800"}`}>
                          {u.name}
                        </span>
                        <span className={`text-[10px] ${isSel ? "text-teal-600" : "text-slate-400"}`}>
                          {u.factor > 0 ? `Isi ${u.factor}` : "Isi 1"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── 4. Pilihan Extra (Addon Groups) ─────────────────── */}
            {hasExtras && (
              <div className="space-y-3">
                {addonGroups.map(group => {
                  const maxSel = group.max_selection || 1;
                  const minSel = group.min_selection || 0;
                  const isRadio = maxSel === 1;
                  const selectedInGroup = (group.addons || []).filter(a => (addonSelections[a.id] || 0) > 0).length;
                  const groupSatisfied = minSel === 0 || selectedInGroup >= minSel;

                  return (
                    <div key={group.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                      {/* Group header */}
                      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <PlusCircle className="w-3.5 h-3.5 text-teal-600" />
                          <span className="text-sm font-bold text-slate-800">{group.name}</span>
                          {minSel > 0 && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              groupSatisfied ? 'bg-teal-100 text-teal-700' : 'bg-rose-100 text-rose-700'
                            }`}>
                              Wajib
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400">
                          {isRadio ? 'Pilih 1' : `Maks ${maxSel}`}
                          {selectedInGroup > 0 && ` · ${selectedInGroup} dipilih`}
                        </span>
                      </div>

                      {/* Addon options */}
                      <div className="p-3 space-y-2">
                        {(group.addons || []).map(addon => {
                          const qty = addonSelections[addon.id] || 0;
                          const isSelected = qty > 0;
                          const selectedInGroup = (group.addons || []).filter(a => (addonSelections[a.id] || 0) > 0).length;
                          const limit = maxSel > 0 ? maxSel : 99;
                          const isOutOfStock = typeof addon.stok === 'number' && addon.stok <= 0;
                          const isDisabled = isOutOfStock || (!isSelected && !isRadio && selectedInGroup >= limit);

                          return (
                            <div
                              key={addon.id}
                              onClick={() => {
                                if (!isDisabled) handleAddonToggle(group, addon);
                              }}
                              className={`w-full flex items-center justify-between p-3 rounded-xl ring-1 transition-all text-left ${
                                isSelected
                                  ? 'ring-emerald-400 bg-emerald-50/60 shadow-sm'
                                  : isDisabled
                                    ? 'ring-slate-100 bg-slate-50/30 opacity-50 cursor-not-allowed'
                                    : 'ring-slate-100 bg-slate-50/50 hover:ring-slate-200 cursor-pointer'
                              }`}
                            >
                              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                {/* Radio / Checkbox indicator */}
                                <span className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  isSelected
                                    ? 'border-emerald-500 bg-emerald-500'
                                    : 'border-slate-300'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z" />
                                    </svg>
                                  )}
                                </span>
                                <div className="min-w-0">
                                  <p className={`text-sm font-semibold ${
                                    isSelected ? 'text-emerald-900' : 'text-slate-700'
                                  }`}>{addon.name}</p>
                                   {showStock && typeof addon.stok === 'number' && (
                                     <p className={`text-[10px] ${addon.stok > 0 ? "text-slate-400" : "text-rose-500 font-semibold"}`}>
                                       {addon.stok > 0 ? `Stok: ${addon.stok}` : "Stok: 0 (Habis)"}
                                     </p>
                                   )}
                                </div>
                              </div>

                              <div className="flex items-center gap-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                <span className={`text-sm font-bold ${
                                  isSelected ? 'text-emerald-700 font-bold' : 'text-slate-500'
                                }`}>
                                  {(addon.sell_price || 0) > 0 ? `+${(addon.sell_price || 0).toLocaleString('id-ID')}` : 'Gratis'}
                                </span>

                                {/* Quantity Control for selected addons */}
                                {isSelected && (
                                  <div className="flex items-center gap-1.5 bg-white ring-1 ring-emerald-300/80 rounded-lg p-0.5 shadow-sm">
                                    <button
                                      type="button"
                                      onClick={() => handleAddonQtyChange(group, addon, -1)}
                                      className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors">
                                      <Minus className="w-2.5 h-2.5" />
                                    </button>
                                    <span className="text-xs font-bold text-slate-800 px-1 min-w-[12px] text-center">{qty}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleAddonQtyChange(group, addon, 1)}
                                      className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors">
                                      <Plus className="w-2.5 h-2.5" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Jumlah Order ──────────────────────────────────── */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 mb-2">
                Jumlah Order
              </p>
              <div className="flex items-center gap-2 bg-white ring-1 ring-slate-200 rounded-xl px-3 py-2 w-fit">
                <button
                  type="button"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={quantity}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1) setQuantity(val);
                  }}
                  min={1}
                  className="w-16 h-8 text-center text-sm font-bold text-slate-900 bg-transparent border-0 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-md [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => setQuantity(q => q + 1)}
                  className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-500 ml-1">{unitLabel}</span>
              </div>
            </div>

          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <div className="px-5 py-3.5 border-t border-slate-100 bg-white flex items-center justify-between gap-3 flex-shrink-0">
          <div className="text-left">
            <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Total</p>
            <p className="text-lg font-bold text-slate-900">{formatCurrency(finalTotal)}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl ring-1 ring-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
              Batal
            </button>
            <button
              disabled={!canConfirm}
              onClick={handleConfirm}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-50 disabled:cursor-not-allowed">
              <ShoppingCart className="w-4 h-4" />
              Tambah ke Keranjang
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
